import { BaeRoute, RouteMap } from '@bae/routes';

import { AppTypes } from '@/app-config/appTypes.ts';

import {
    ComparisonSections,
    FinancialSections,
    InventorySections,
    KpiSections,
} from './types.ts';

interface AppConfig {
    enabledScenarioRoutes: BaeRoute[];
    shareableTabs: BaeRoute[];
    kpiSections?: KpiSections[];
    financialSections?:
    | (FinancialSections | { section: FinancialSections; title: string })[]
    | null;
    inventorySections?:
    | (InventorySections | { section: InventorySections; title: string })[]
    | null;
    comparisonSections?: ComparisonSections[];
    showVendorIntegration?: boolean;
    showPricingPresets?: boolean;
    showLicenseSavings?: boolean;
    hideAccessPointsTab?: boolean;
    useExpenseDefaultsMatrix?: boolean;
    enableAdvancedWanEdgeFeatures?: boolean;
    showVodafoneServices?: boolean;
    enableAgentChat?: boolean;
}

interface Config {
    [AppTypes.STANDARD]: AppConfig;
    [AppTypes.NEW_FRONT]: AppConfig;
    [AppTypes.GLOBAL_FABRIC]: AppConfig;
    [AppTypes.MIST]: AppConfig;
}

// The new front reuses the standard scenario tabs, so both apps share this entry.
const STANDARD_CONFIG: AppConfig = {
    shareableTabs: [
        RouteMap.kpis,
        RouteMap.services,
        RouteMap.expenses,
        RouteMap.financials,
        RouteMap.topology,
        RouteMap.inventory,
        RouteMap.connmap,
        {
            label: 'Microdata',
            path: 'microdata',
        },
    ],
    enabledScenarioRoutes: [
        RouteMap.kpis,
        RouteMap.services,
        RouteMap.expenses,
        RouteMap.financials,
        RouteMap.topology,
        RouteMap.inventory,
        RouteMap.connmap,
        RouteMap.microdata,
        RouteMap.modelDesigner,
    ],
    inventorySections: [
        InventorySections?.serversEvolution,
        InventorySections?.serversInventory,
        InventorySections?.inventoryResources,
        InventorySections?.inventoryFTE,
    ],
    enableAgentChat: true,
};

export const config: Config = {
    [AppTypes.STANDARD]: STANDARD_CONFIG,
    [AppTypes.NEW_FRONT]: STANDARD_CONFIG,
    [AppTypes.GLOBAL_FABRIC]: {
        enabledScenarioRoutes: [
            RouteMap.kpis,
            RouteMap.expenses,
            RouteMap.financials,
            RouteMap.topology,
            RouteMap.inventory,
        ],
        shareableTabs: [
            RouteMap.kpis,
            RouteMap.expenses,
            RouteMap.financials,
            RouteMap.topology,
            RouteMap.inventory,
        ],
        kpiSections: [KpiSections.financialIndicators, KpiSections.cashFlow],
        financialSections: [
            FinancialSections.cashFlow,
            FinancialSections.operatingIncome,
            {
                section: FinancialSections.averagesEndPoint,
                title: 'Averages per Global Fabric site',
            },
            FinancialSections.averagesDataRate,
        ],
        inventorySections: [
            InventorySections.inventoryResources,
            InventorySections.inventoryFTE,
        ],
        comparisonSections: [
            ComparisonSections.comparisonDescription,
            ComparisonSections.financialIndicators,
            ComparisonSections.cashFlowComparison,
            ComparisonSections.indicatorsEvolution,
            ComparisonSections.fteComparisonTable,
        ],
    },
    [AppTypes.MIST]: {
        enabledScenarioRoutes: [
            RouteMap.settings,
            RouteMap.kpis,
            RouteMap.expenses,
            RouteMap.financials,
            RouteMap.inventory,
            RouteMap.microdata,

            import.meta.env.VITE_BAE_ENVIRONMENT !== 'stable' &&
            RouteMap.modelDesigner,
        ],
        shareableTabs: [
            RouteMap.kpis,
            RouteMap.expenses,
            RouteMap.financials,
            RouteMap.inventory,
        ],
        kpiSections: [
            KpiSections.financialIndicators,
            KpiSections.cashFlow,
            KpiSections.indicatorsEvolution,
        ],
        financialSections: [
            FinancialSections.cashFlow,
            FinancialSections.operatingIncome,
            {
                section: FinancialSections.averagesEndPoint,
                title: 'Averages per Device',
            },
            {
                section: FinancialSections.averagesTenant,
                title: 'Averages per Site',
            },
            FinancialSections.averagesDataRate,
        ],
        inventorySections: [
            {
                section: InventorySections.inventoryResources,
                title: 'Network Services',
            },
            InventorySections.inventoryFTE,
        ],
        comparisonSections: [
            ComparisonSections.comparisonDescription,
            ComparisonSections.financialIndicators,
            ComparisonSections.cashFlowComparison,
            ComparisonSections.indicatorsEvolution,
            ComparisonSections.fteComparisonTable,
        ],
        showVendorIntegration: true,
        showPricingPresets: true,
        showLicenseSavings: true,
        hideAccessPointsTab: false,
        useExpenseDefaultsMatrix: true,
        enableAdvancedWanEdgeFeatures: true,
        showVodafoneServices: true,
        enableAgentChat: true,
    },
};

// Domain (alias of the unstable CloudFront) that serves the new front from the
// standard build, without a dedicated bucket or build mode.
export const NEW_FRONT_HOSTNAME = 'new-front-unstable.acganalytics.com';

export const getAppType = (): AppTypes => {
    if (window.location.hostname === NEW_FRONT_HOSTNAME) {
        localStorage.setItem('appType', AppTypes.NEW_FRONT);
        return AppTypes.NEW_FRONT;
    }

    const envAppType = (import.meta.env.VITE_APP_TYPE ||
        'standard') as AppTypes;

    if (envAppType) {
        localStorage.setItem('appType', envAppType);
        return envAppType;
    }

    throw new Error('App type not defined');
};

export const getDisabledFinancialStatements = () => {
    const appType = getAppType();

    if (appType === AppTypes.MIST || appType === AppTypes.GLOBAL_FABRIC) {
        return ['Revenue', 'EBITDA'];
    }

    return null;
};

export const getIndicatorsEvolutionDisabledOptions = () => {
    const appType = getAppType();

    const DISABLED_INDICATORS_BY_APP: { [key in AppTypes]?: string[] } = {
        [AppTypes.MIST]: ['margin', 'revenue', 'cashflow', 'ebitda', 'ebit'],
        [AppTypes.GLOBAL_FABRIC]: [
            'margin',
            'revenue',
            'cashflow',
            'ebitda',
            'ebit',
        ],
    };

    const DISABLED_KPIS_BY_APP: { [key in AppTypes]?: string[] } = {
        [AppTypes.MIST]: ['endpoints', 'datavolume', 'users', 'sessions'],
        [AppTypes.GLOBAL_FABRIC]: [
            'endpoints',
            'datavolume',
            'users',
            'sessions',
        ],
    };

    const disabledIndicators = DISABLED_INDICATORS_BY_APP[appType] || [];
    const disabledKpis = DISABLED_KPIS_BY_APP[appType] || [];

    return {
        disabledIndicators,
        disabledKpis,
    };
};

export function getEnabledAppFeatures(): AppConfig {
    const appType = getAppType();

    if (!appType || !config[appType]) {
        throw new Error('Invalid or nonexistent app type');
    }

    return config[appType];
}
